<!--
~ Copyright (C) 2023 DV Bern AG, Switzerland
~
~ This program is free software: you can redistribute it and/or modify
~ it under the terms of the GNU Affero General Public License as
~ published by the Free Software Foundation, either version 3 of the
~ License, or (at your option) any later version.
~
~ This program is distributed in the hope that it will be useful,
~ but WITHOUT ANY WARRANTY; without even the implied warranty of
~ MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
~ GNU Affero General Public License for more details.
~
~ You should have received a copy of the GNU Affero General Public License
~ along with this program. If not, see <https://www.gnu.org/licenses/>.
-->
<div class="dv-table">
    <div class="row view-title">
        <div class="col-md-12">
            @if (title) {
                <h2>{{ title }} ({{ totalItems }})</h2>
            } @else {
                <h2>{{ 'FAELLE_LIST' | translate }} ({{ totalItems }})</h2>
            }
        </div>
    </div>
    <div class="row margin-top-10">
        <div class="col-md-8">
            <button (click)="resetFilter()" class="dv-btn" id="refreshIcon">
                <i class="fa fa-undo"></i>
                {{ 'FILTER_ZURUECKSETZEN' | translate }}
            </button>
        </div>
        <div class="col-md-4">
            <div id="box-right">
                @if (showSearchInaktivePerioden()) {
                    <dv-checkbox-x
                        [model]="searchInaktivePerioden"
                        [marginClass]="'no-margin-bottom'"
                        (modelChange)="changeSearchInaktivePerioden($event)"
                        [displayBisher]="false"
                        [label]="'SEARCH_INAKTIVE_PERIODEN' | translate"
                    >
                    </dv-checkbox-x>
                }
            </div>
        </div>
    </div>

    <div class="mat-elevation-z0">
        <mat-table
            [dataSource]="datasource"
            matSort
            [matSortActive]="'aenderungsdatum'"
            [matSortDirection]="'asc'"
            (matSortChange)="sortData($event)"
        >
            <ng-container matColumnDef="fallNummer">
                <mat-header-cell
                    mat-sort-header="fallNummer"
                    *matHeaderCellDef
                    class="ellipsis"
                >
                    <span class="no-padding-left" translate>FALL</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element" class="ellipsis">
                    <span [title]="addZerosToFallnummer(element.fallNummer)">{{
                        addZerosToFallnummer(element.fallNummer)
                    }}</span>
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="gemeinde">
                <mat-header-cell
                    mat-sort-header="gemeinde"
                    *matHeaderCellDef
                    class="ellipsis"
                >
                    <span translate>GEMEINDE</span></mat-header-cell
                >
                <mat-cell *matCellDef="let element" class="ellipsis">
                    <span [title]="element.gemeinde">{{
                        element.gemeinde
                    }}</span>
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="familienName">
                <mat-header-cell
                    mat-sort-header="familienName"
                    *matHeaderCellDef
                    class="ellipsis"
                >
                    <span translate>FAMILIE</span></mat-header-cell
                >
                <mat-cell *matCellDef="let element" class="ellipsis">
                    <span [title]="element.familienName">{{
                        element.familienName
                    }}</span></mat-cell
                >
            </ng-container>

            <ng-container matColumnDef="kinder">
                <mat-header-cell mat-sort-header="kinder" *matHeaderCellDef
                    ><span translate>KINDER</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element"
                    ><span [title]="element.kinder.join(', ')">{{
                        element.kinder.join(', ')
                    }}</span></mat-cell
                >
            </ng-container>

            <ng-container matColumnDef="antragTyp">
                <mat-header-cell mat-sort-header="antragTyp" *matHeaderCellDef
                    ><span translate>TYP</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    @if (
                        element.hasBesitzer?.call() && !element.isSozialdienst
                    ) {
                        <i class="fa fa-desktop margin-ri"></i>
                    }
                    @if (
                        !element.hasBesitzer?.call() && !element.isSozialdienst
                    ) {
                        <i class="fa fa-file-text-o margin-ri"></i>
                    }
                    @if (element.isSozialdienst) {
                        <i class="fa fa-handshake-o margin-ri"></i>
                    }
                    <span
                        class="margin-left-5"
                        [title]="element.antragTyp | translate"
                    >
                        {{ getAntragTypBezeichnung(element) }}</span
                    ></mat-cell
                >
            </ng-container>

            <ng-container matColumnDef="periode">
                <mat-header-cell
                    mat-sort-header="gesuchsperiodeString"
                    *matHeaderCellDef
                    ><span translate>PERIODE</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element"
                    ><span [title]="element.periodenString">{{
                        element.periodenString
                    }}</span>
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="aenderungsdatum">
                <mat-header-cell
                    mat-sort-header="aenderungsdatum"
                    *matHeaderCellDef
                    ><span translate>GEANDERT</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    <span
                        [title]="element.aenderungsdatum.format('DD.MM.YYYY')"
                        >{{
                            element.aenderungsdatum.format('DD.MM.YYYY')
                        }}</span
                    ></mat-cell
                >
            </ng-container>

            <ng-container matColumnDef="eingangsdatumSTV">
                <mat-header-cell
                    mat-sort-header="eingangsdatumSTV"
                    *matHeaderCellDef
                    ><span translate>EINGANG</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    <span
                        [title]="element.eingangsdatumSTV?.format('DD.MM.YYYY')"
                        >{{
                            element.eingangsdatumSTV?.format('DD.MM.YYYY')
                        }}</span
                    ></mat-cell
                >
            </ng-container>

            <ng-container matColumnDef="gemeindeAntragFirstEinreichedatum">
                <mat-header-cell
                    mat-sort-header="gemeindeAntragFirstEinreichedatum"
                    *matHeaderCellDef
                    ><span translate>EINGEREICHT_AM</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    <span [title]="element.gemeindeAntragFirstEinreichedatum">{{
                        element.gemeindeAntragFirstEinreichedatum
                    }}</span></mat-cell
                >
            </ng-container>

            <ng-container matColumnDef="status">
                <mat-header-cell mat-sort-header="status" *matHeaderCellDef
                    ><span translate>STATUS</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    <span [title]="element.status | translate">{{
                        element.status | translate
                    }}</span></mat-cell
                >
            </ng-container>

            <ng-container matColumnDef="internePendenz">
                <mat-header-cell
                    mat-sort-header="internePendenz"
                    *matHeaderCellDef
                    ><span translate>INTERNE_PENDENZ_KURZ</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    @if (element.internePendenz) {
                        <span [title]="'INTERNE_PENDENZ' | translate">
                            @if (!element.internePendenzAbgelaufen) {
                                <i
                                    class="fa fa-thumb-tack pendenz-icon"
                                    aria-hidden="true"
                                ></i>
                            }
                            @if (element.internePendenzAbgelaufen) {
                                <i
                                    class="fa fa-thumb-tack pendenz-icon abgelaufen"
                                    aria-hidden="true"
                                ></i>
                            }
                        </span>
                    }
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="dokumenteHochgeladen">
                <mat-header-cell
                    mat-sort-header="dokumenteHochgeladen"
                    *matHeaderCellDef
                    ><span translate>DOKUMENTE_HOCHGELADEN_KURZ</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    @if (element.dokumenteHochgeladen) {
                        <span [title]="'DOKUMENTE_HOCHGELADEN' | translate">
                            <i class="fa fa-file-o" aria-hidden="true"></i>
                        </span>
                    }
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="angebote">
                <mat-header-cell mat-sort-header="angebote" *matHeaderCellDef
                    ><span translate>ANGEBOT</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element"
                    ><span
                        [title]="createAngeboteString(element.angebote) | async"
                    >
                        @for (
                            angebot of element.angebote;
                            track angebot;
                            let i = $index
                        ) {
                            <span
                                >{{ angebot | translate
                                }}{{
                                    i < element.angebote.length - 1 ? ',' : ''
                                }}</span
                            >
                        }
                    </span></mat-cell
                >
            </ng-container>

            <ng-container matColumnDef="institutionen">
                <mat-header-cell
                    mat-sort-header="institutionen"
                    *matHeaderCellDef
                    ><span translate>INSTITUTION</span>
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                    <span [title]="element.institutionen">{{
                        element.institutionen
                    }}</span></mat-cell
                >
            </ng-container>

            <ng-container matColumnDef="remove">
                <mat-header-cell *matHeaderCellDef></mat-header-cell>
                <mat-cell
                    *matCellDef="let element"
                    (click)="$event.stopPropagation()"
                >
                    @if (element.antragAbgeschlossen === false) {
                        <button
                            type="button"
                            class="dv-btn dv-btn-operation"
                            (click)="removeClicked.emit(element)"
                        >
                            <i class="fa fa-lg fa-trash-o"></i>
                        </button>
                    }
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="verantwortlicheTS">
                <mat-header-cell
                    mat-sort-header="verantwortlicherTS"
                    *matHeaderCellDef
                    translate
                >
                    <i class="fa fa-graduation-cap"></i
                    ><span translate>VERANTWORTLICH</span></mat-header-cell
                >
                <mat-cell *matCellDef="let element">
                    <span [title]="element.verantwortlicheTS">{{
                        element.verantwortlicheTS
                    }}</span></mat-cell
                >
            </ng-container>

            <ng-container matColumnDef="verantwortlicheBG">
                <mat-header-cell
                    mat-sort-header="verantwortlicherBG"
                    *matHeaderCellDef
                >
                    <i class="fa fa-gift"></i
                    ><span translate>VERANTWORTLICH</span></mat-header-cell
                >
                <mat-cell *matCellDef="let element">
                    <span [title]="element.verantwortlicheBG">{{
                        element.verantwortlicheBG
                    }}</span></mat-cell
                >
            </ng-container>

            <ng-container matColumnDef="verantwortlicheGemeinde">
                <mat-header-cell
                    mat-sort-header="verantwortlicheGemeinde"
                    *matHeaderCellDef
                >
                    <span translate>VERANTWORTLICH</span></mat-header-cell
                >
                <mat-cell *matCellDef="let element">
                    <span [title]="getVerantwortlicheBgAndTs(element)">{{
                        getVerantwortlicheBgAndTs(element)
                    }}</span>
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="verantwortlicherGemeindeantraege">
                <mat-header-cell
                    mat-sort-header="verantwortlicherGemeindeantraege"
                    *matHeaderCellDef
                >
                    <span translate>VERANTWORTLICH</span></mat-header-cell
                >
                <mat-cell *matCellDef="let element">
                    <span
                        [title]="
                            element.verantwortlicherGemeindeantraege?.getFullName()
                        "
                        >{{
                            element.verantwortlicherGemeindeantraege?.getFullName()
                        }}</span
                    >
                </mat-cell>
            </ng-container>

            <ng-container matColumnDef="fallNummer-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field class="dv-faelle-mat-form-field">
                        <input
                            [value]="filterPredicate.fallNummer || null"
                            matInput
                            (keyup)="filterFall($event.target.value)"
                        />
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="gemeinde-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field
                        class="dv-faelle-mat-form-field"
                        floatLabel="never"
                    >
                        <mat-select
                            [value]="filterPredicate.gemeinde"
                            (selectionChange)="filterGemeinde($event.value)"
                            disableOptionCentering
                            panelClass="mat-select-panel1"
                            [placeholder]="'ALLE' | translate"
                            data-test="gemeindeantraege-gemeinde-tabelle"
                        >
                            <mat-option><span translate>ALLE</span></mat-option>
                            @for (gemeinde of gemeindenList; track gemeinde) {
                                <mat-option [value]="gemeinde.name">{{
                                    gemeinde.name
                                }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="antragTyp-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field
                        class="dv-faelle-mat-form-field"
                        floatLabel="never"
                    >
                        <mat-select
                            [value]="filterPredicate.antragTyp"
                            (selectionChange)="filterType($event.value)"
                            panelClass="mat-select-panel1"
                            [placeholder]="'ALLE' | translate"
                            data-test="gemeindeantraege-typ-tabelle"
                        >
                            <mat-option selected
                                ><span translate>ALLE</span></mat-option
                            >
                            @for (type of getAntragTypen(); track type) {
                                <mat-option
                                    [value]="type"
                                    [attr.data-test]="type"
                                >
                                    <span translate>{{
                                        type
                                    }}</span></mat-option
                                >
                            }
                        </mat-select>
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="status-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field
                        class="dv-faelle-mat-form-field"
                        floatLabel="never"
                    >
                        <mat-select
                            [value]="filterPredicate.status"
                            (selectionChange)="filterStatus($event.value)"
                            panelClass="mat-select-panel1"
                            [placeholder]="'ALLE' | translate"
                        >
                            <mat-option selected
                                ><span translate>ALLE</span></mat-option
                            >
                            @for (status of getAntragStatus(); track status) {
                                <mat-option [value]="status"
                                    ><span translate>{{
                                        status
                                    }}</span></mat-option
                                >
                            }
                        </mat-select>
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="angebote-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field
                        class="dv-faelle-mat-form-field"
                        floatLabel="never"
                    >
                        <mat-select
                            [value]="filterPredicate.angebote"
                            (selectionChange)="filterAngebot($event.value)"
                            panelClass="mat-select-panel1"
                            [placeholder]="'ALLE' | translate"
                        >
                            <mat-option selected
                                ><span translate>ALLE</span></mat-option
                            >
                            @for (
                                angebot of getBetreuungsangebotTypen();
                                track angebot
                            ) {
                                <mat-option [value]="angebot"
                                    ><span translate>{{
                                        angebot
                                    }}</span></mat-option
                                >
                            }
                        </mat-select>
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="periode-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field
                        class="dv-faelle-mat-form-field"
                        floatLabel="never"
                    >
                        <mat-select
                            [value]="filterPredicate.gesuchsperiodeString"
                            (selectionChange)="filterPeriode($event.value)"
                            panelClass="mat-select-panel1"
                            [placeholder]="'ALLE' | translate"
                            data-test="gemeindeantraege-periode-tabelle"
                        >
                            <mat-option selected
                                ><span translate>ALLE</span></mat-option
                            >
                            @for (
                                periode of gesuchsperiodenList;
                                track periode
                            ) {
                                <mat-option
                                    [value]="periode"
                                    [attr.data-test]="'periode-' + periode"
                                    >{{ periode }}</mat-option
                                >
                            }
                        </mat-select>
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="internePendenz-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field
                        class="dv-faelle-mat-form-field"
                        floatLabel="never"
                    >
                        <mat-select
                            (selectionChange)="
                                filterInternePendenz($event.value)
                            "
                            [value]="filterPredicate.internePendenz"
                            panelClass="mat-select-panel1"
                            [placeholder]="'ALLE' | translate"
                        >
                            <mat-option selected
                                ><span translate>ALLE</span></mat-option
                            >
                            <mat-option [value]="true"
                                ><span translate>LABEL_JA</span></mat-option
                            >
                            <mat-option [value]="false"
                                ><span translate>LABEL_NEIN</span></mat-option
                            >
                        </mat-select>
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="dokumenteHochgeladen-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field
                        class="dv-faelle-mat-form-field"
                        floatLabel="never"
                    >
                        <mat-select
                            [value]="filterPredicate.dokumenteHochgeladen"
                            (selectionChange)="
                                filterDocumentsUploaded($event.value)
                            "
                            panelClass="mat-select-panel1"
                            [placeholder]="'ALLE' | translate"
                        >
                            <mat-option selected
                                ><span translate>ALLE</span></mat-option
                            >
                            <mat-option [value]="true"
                                ><span translate>LABEL_JA</span></mat-option
                            >
                            <mat-option [value]="false"
                                ><span translate>LABEL_NEIN</span></mat-option
                            >
                        </mat-select>
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="familienName-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field class="dv-faelle-mat-form-field">
                        <input
                            [value]="filterPredicate.familienName || null"
                            matInput
                            (keyup)="filterFamilie($event.target.value)"
                        />
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="kinder-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field class="dv-faelle-mat-form-field">
                        <input
                            [value]="filterPredicate.kinder || null"
                            matInput
                            (keyup)="filterKinder($event.target.value)"
                        />
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="aenderungsdatum-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field class="dv-faelle-mat-form-field">
                        <input
                            [value]="filterPredicate.aenderungsdatum || null"
                            matInput
                            (keyup)="filterGeaendert($event.target.value)"
                        />
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="eingangsdatumSTV-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field class="dv-faelle-mat-form-field">
                        <input
                            [value]="filterPredicate.eingangsdatumSTV || null"
                            matInput
                            (keyup)="filterEingangSTV($event.target.value)"
                        />
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container
                matColumnDef="gemeindeAntragFirstEinreichedatum-filter"
            >
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field class="dv-faelle-mat-form-field">
                        <input
                            [value]="
                                filterPredicate.gemeindeAntragFirstEinreichedatum ||
                                null
                            "
                            matInput
                            (keyup)="
                                filterGemeindeAntragFirstEinreichedatum(
                                    $event.target.value
                                )
                            "
                        />
                    </mat-form-field>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="institutionen-filter">
                <mat-header-cell *matHeaderCellDef="let column">
                    <mat-form-field
                        class="dv-faelle-mat-form-field"
                        floatLabel="never"
                    >
                        <input
                            matInput
                            [value]="filterPredicate.institutionen || null"
                            (keyup)="filterInstitution($event.target.value)"
                            [matAutocomplete]="auto"
                        />
                    </mat-form-field>

                    <mat-autocomplete
                        #auto="matAutocomplete"
                        (optionSelected)="
                            filterInstitution($event.option.value)
                        "
                    >
                        @for (
                            institution of institutionenList$ | async;
                            track institution
                        ) {
                            <mat-option
                                [value]="institution.name"
                                style="font-size: 1.6rem"
                            >
                                {{ institution.name }}</mat-option
                            >
                        }
                    </mat-autocomplete>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="verantwortlicheTS-filter" st-table="">
                <mat-header-cell *matHeaderCellDef="let column">
                    <div
                        dvNewUserSelect
                        [selectedUser]="initialTsGemeindeUser"
                        [showSelectionAll]="true"
                        [angular2]="true"
                        [initialAll]="true"
                        (userChanged)="filterVerantwortlicheTS($event.user)"
                        [schulamt]="true"
                    ></div>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="verantwortlicheBG-filter" st-table="">
                <mat-header-cell *matHeaderCellDef="let column">
                    <div
                        dvNewUserSelect
                        [showSelectionAll]="true"
                        [selectedUser]="initialBgGemeindeUser"
                        [angular2]="true"
                        [initialAll]="true"
                        (userChanged)="filterVerantwortlicheBG($event.user)"
                    ></div>
                </mat-header-cell>
            </ng-container>

            <ng-container
                matColumnDef="verantwortlicheGemeinde-filter"
                st-table=""
            >
                <mat-header-cell *matHeaderCellDef="let column">
                    <div
                        dvNewUserSelect
                        [showSelectionAll]="true"
                        [selectedUser]="initialGemeindeUser"
                        [angular2]="true"
                        [initialAll]="true"
                        (userChanged)="
                            filterVerantwortlicheGemeinde($event.user)
                        "
                        [sachbearbeiterGemeinde]="true"
                    ></div>
                </mat-header-cell>
            </ng-container>

            <ng-container
                matColumnDef="verantwortlicherGemeindeantraege-filter"
                st-table=""
            >
                <mat-header-cell *matHeaderCellDef="let column">
                    <div
                        dvNewUserSelect
                        [showSelectionAll]="true"
                        [angular2]="true"
                        [initialAll]="true"
                        [userList]="userListGemeindeantraege"
                        [useDefaultUserLists]="false"
                        (userChanged)="
                            filterVerantwortlicherGemeindeantraege($event.user)
                        "
                    ></div>
                </mat-header-cell>
            </ng-container>

            <ng-container matColumnDef="remove-filter">
                <mat-header-cell
                    *matHeaderCellDef="let column"
                ></mat-header-cell>
            </ng-container>

            <mat-header-row
                *matHeaderRowDef="displayedColumns"
                class="dv-faelle-mat-header-row"
            ></mat-header-row>
            <mat-header-row
                class="dv-faelle-mat-header-row"
                *matHeaderRowDef="filterColumns"
            ></mat-header-row>
            <mat-row
                *matRowDef="
                    let element;
                    columns: displayedColumns;
                    let index = index
                "
                [attr.data-test]="
                    'antrag-entry#' + addZerosToFallnummer(element.fallNummer)
                "
                (click)="onEditClicked(element, $event)"
                class="pointer-cursor"
            ></mat-row>
        </mat-table>

        @if (!disablePagination) {
            <div class="bottom-pagination">
                <nav>
                    <ul class="pagination">
                        @for (index of paginationItems; track index) {
                            <li [ngClass]="{active: index - 1 === page}">
                                <a
                                    (click)="
                                        handlePagination({
                                            pageIndex: index - 1,
                                            pageSize: pageSize
                                        })
                                    "
                                    >{{ index }}</a
                                >
                            </li>
                        }
                    </ul>
                </nav>
            </div>
        }
    </div>
</div>
